import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import axios from '../../interceptors/adminAxios';

interface AdminDevice {
  id: number; device_id: string; display_name: string;
  last_seen_at: string | null; app_version: string | null;
  ip_address: string | null; company_id: string;
  company_display_name: string; company_color: string; online: boolean;
}

interface OrphanDevice {
  device_id: string; app_version: string | null; ip_address: string | null;
  last_seen_at: string; first_seen_at: string; online: boolean;
}

interface PendingActivation {
  activation_code: string; expires_at: string; created_at: string; expired: boolean;
}

interface AdminCompany {
  id: number; name: string; display_name: string; color: string;
}

interface Props {
  onDeviceClick: (deviceId: string) => void;
}

const ONLINE_MS = 2 * 60 * 1000;
const isOnline = (last: string | null) => !!last && Date.now() - new Date(last).getTime() < ONLINE_MS;

const fmtRelative = (d: string | null) => {
  if (!d) return 'never';
  const s = Math.round((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const fmtDate = (d: string) => new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

const IcoScreen = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const glassCard = { background: 'var(--glass-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid var(--glass-border)', borderRadius: '14px', overflow: 'hidden' };

const tabSx = (active: boolean) => ({
  px: '12px', py: '5px', borderRadius: '7px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  bgcolor: active ? 'var(--selected-bg)' : 'transparent',
  border: `1px solid ${active ? 'var(--selected-border)' : 'transparent'}`,
  color: active ? 'var(--text-primary)' : 'var(--text-disabled)',
  userSelect: 'none',
});

const BULK_FIELDS: { value: string; label: string; type: 'boolean' | 'orient' | 'number' | 'company' }[] = [
  { value: 'company_id',        label: 'Company',           type: 'company' },
  { value: 'kiosk_mode',        label: 'Kiosk Mode',        type: 'boolean' },
  { value: 'disable_standby',   label: 'Disable Standby',   type: 'boolean' },
  { value: 'autostart_on_boot', label: 'Autostart on Boot', type: 'boolean' },
  { value: 'debug_mode',        label: 'Debug Mode',        type: 'boolean' },
  { value: 'screen_orientation',label: 'Orientation',       type: 'orient'  },
  { value: 'content_rotation',  label: 'Content Rotation',  type: 'orient'  },
  { value: 'cache_ttl_days',         label: 'Cache TTL (days)',        type: 'number'  },
  { value: 'offline_timeout_days',   label: 'Offline Timeout (days)',   type: 'number'  },
];

const ORIENT_LABELS = ['Landscape', 'Portrait', 'Reverse Landscape', 'Reverse Portrait'];

const AdminDevicesPage: React.FC<Props> = ({ onDeviceClick }) => {
  const [tab, setTab]         = useState<'all' | 'unregistered'>('all');
  const [devices, setDevices] = useState<AdminDevice[]>([]);
  const [orphans, setOrphans] = useState<OrphanDevice[]>([]);
  const [pending, setPending] = useState<PendingActivation[]>([]);
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [bulkField, setBulkField] = useState(BULK_FIELDS[0].value);
  const [bulkValue, setBulkValue] = useState<string>('');
  const [bulkApplying, setBulkApplying] = useState(false);
  const [companies, setCompanies] = useState<AdminCompany[]>([]);

  const [addOpen, setAddOpen]       = useState(false);
  const [addName, setAddName]       = useState('');
  const [addCompany, setAddCompany] = useState('');
  const [addSaving, setAddSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [devRes, unregRes] = await Promise.all([
        axios.get<AdminDevice[]>('/admin/devices'),
        axios.get<{ orphans: OrphanDevice[]; pending: PendingActivation[] }>('/admin/unregistered'),
      ]);
      setDevices(devRes.data);
      setOrphans(unregRes.data.orphans);
      setPending(unregRes.data.pending);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get<AdminCompany[]>('/admin/companies').then(r => {
      setCompanies(r.data);
      if (BULK_FIELDS[0].type === 'company' && r.data.length > 0) {
        setBulkValue(r.data[0].name);
      }
    }).catch(() => {});
  }, []);

  const filtered = devices.filter(d =>
    d.display_name.toLowerCase().includes(search.toLowerCase()) ||
    d.device_id.toLowerCase().includes(search.toLowerCase()) ||
    d.company_display_name.toLowerCase().includes(search.toLowerCase()) ||
    (d.ip_address ?? '').includes(search)
  );

  const online = devices.filter(d => isOnline(d.last_seen_at)).length;

  const toggleSelect = (deviceId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      next.has(deviceId) ? next.delete(deviceId) : next.add(deviceId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(d => d.device_id)));
    }
  };

  const handleDeleteOrphan = async (device_id: string) => {
    await axios.delete(`/admin/orphans/${device_id}`);
    setOrphans(prev => prev.filter(o => o.device_id !== device_id));
  };

  const handleBulkApply = async () => {
    if (selected.size === 0) return;
    setBulkApplying(true);
    try {
      const fieldMeta = BULK_FIELDS.find(f => f.value === bulkField)!;
      let value: any = bulkValue;
      if (fieldMeta.type === 'boolean') value = bulkValue === 'true';
      if (fieldMeta.type === 'number' || fieldMeta.type === 'orient') value = parseInt(bulkValue, 10);
      // company type: value stays as string (company name)
      await axios.post('/admin/devices/bulk-update', { device_ids: Array.from(selected), field: bulkField, value });
      setSelected(new Set());
      await load();
    } finally {
      setBulkApplying(false);
    }
  };

  const currentBulkFieldMeta = BULK_FIELDS.find(f => f.value === bulkField)!;

  const handleCreate = async () => {
    setAddSaving(true);
    try {
      const body: { display_name?: string; company_id?: string } = {};
      if (addName.trim())    body.display_name = addName.trim();
      if (addCompany.trim()) body.company_id   = addCompany.trim();
      const { data } = await axios.post('/admin/devices', body);
      setAddOpen(false);
      setAddName('');
      setAddCompany('');
      await load();
      onDeviceClick(data.device_id);
    } finally {
      setAddSaving(false);
    }
  };

  const inpSx = { background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 7, padding: '4px 8px', fontSize: 12, color: 'var(--text-primary)', outline: 'none', fontFamily: 'inherit' };

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: '16px', gap: '12px' }}>

      {/* Tabs + KPI */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        <Box sx={tabSx(tab === 'all')} onClick={() => setTab('all')}>All Devices</Box>
        <Box sx={{ ...tabSx(tab === 'unregistered'), display: 'flex', alignItems: 'center', gap: '5px' }}
          onClick={() => setTab('unregistered')}>
          Unregistered
          {(orphans.length + pending.length) > 0 && (
            <Box sx={{ fontSize: 10, fontWeight: 700, px: '5px', borderRadius: '10px', background: 'rgba(251,191,36,.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,.25)' }}>
              {orphans.length + pending.length}
            </Box>
          )}
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
          {tab === 'all' && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: '10px', py: '4px', borderRadius: '8px', background: 'rgba(74,222,128,.08)', border: '1px solid rgba(74,222,128,.15)' }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80' }} />
                <Typography sx={{ fontSize: 12, color: '#4ade80' }}>Online: <b>{online}</b></Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: '10px', py: '4px', borderRadius: '8px', background: 'var(--glass-hover)', border: '1px solid var(--glass-border)' }}>
                <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>Total: <b style={{ color: 'var(--text-primary)' }}>{devices.length}</b></Typography>
              </Box>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
                style={{ ...inpSx, width: 180 } as React.CSSProperties} />
            </>
          )}
          {tab === 'all' && (
            <Box component="button" onClick={() => { setAddName(''); setAddCompany(companies[0]?.name ?? ''); setAddOpen(true); }}
              sx={{ display: 'flex', alignItems: 'center', gap: '5px', px: '10px', height: 30, borderRadius: '7px', border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', '&:hover': { background: 'var(--accent-light)', borderColor: 'var(--accent-light)' } }}>
              + Add Device
            </Box>
          )}
          <Box component="button" onClick={load}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: '7px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-secondary)', cursor: 'pointer', '&:hover': { color: 'var(--accent-light)', borderColor: 'var(--accent)', background: 'var(--glass-hover)' } }}>
            ↻
          </Box>
        </Box>
      </Box>

      {/* All devices tab */}
      {tab === 'all' && (
        <Box sx={{ ...glassCard, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header row */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '36px 32px 1fr 160px 120px 90px 100px', px: '14px', py: '7px', background: 'var(--glass-hover)', borderBottom: '1px solid var(--divider)', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <input type="checkbox"
                checked={filtered.length > 0 && selected.size === filtered.length}
                onChange={toggleSelectAll}
                style={{ cursor: 'pointer', accentColor: 'var(--accent)', width: 14, height: 14 }}
              />
            </Box>
            {['', 'Device', 'Company', 'IP', 'Version', 'Last seen'].map((h, i) => (
              <Typography key={i} sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)', display: 'flex', alignItems: 'center' }}>{h}</Typography>
            ))}
          </Box>

          {/* Scrollable body */}
          <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {loading && <Box sx={{ px: '18px', py: '20px' }}><Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>Loading…</Typography></Box>}
            {!loading && filtered.length === 0 && <Box sx={{ px: '18px', py: '20px' }}><Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>No devices found.</Typography></Box>}
            {filtered.map(dev => {
              const on = isOnline(dev.last_seen_at);
              const isSel = selected.has(dev.device_id);
              return (
                <Box key={dev.id}
                  onClick={() => onDeviceClick(dev.device_id)}
                  sx={{ display: 'grid', gridTemplateColumns: '36px 32px 1fr 160px 120px 90px 100px', px: '14px', py: '9px', borderBottom: '1px solid var(--divider)', alignItems: 'center', cursor: 'pointer', background: isSel ? 'var(--selected-bg)' : 'transparent', '&:hover': { background: isSel ? 'var(--selected-bg)' : 'var(--row-hover)' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={e => toggleSelect(dev.device_id, e)}>
                    <input type="checkbox" checked={isSel} readOnly
                      style={{ cursor: 'pointer', accentColor: 'var(--accent)', width: 14, height: 14, pointerEvents: 'none' }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: on ? '#4ade80' : '#94a3b8', boxShadow: on ? '0 0 6px #4ade80' : 'none' }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <Box sx={{ color: 'var(--text-secondary)', flexShrink: 0 }}><IcoScreen /></Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dev.display_name}</Typography>
                      <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace' }}>{dev.device_id}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: dev.company_color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dev.company_display_name}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace' }}>{dev.ip_address ?? '—'}</Typography>
                  <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace' }}>{dev.app_version ?? '—'}</Typography>
                  <Typography sx={{ fontSize: 12, color: on ? '#4ade80' : 'var(--text-disabled)' }}>{fmtRelative(dev.last_seen_at)}</Typography>
                </Box>
              );
            })}
          </Box>

          {/* Bulk-action footer */}
          {selected.size > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '16px', py: '10px', borderTop: '1px solid var(--divider)', background: 'var(--glass-hover)', flexShrink: 0 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-light)', flexShrink: 0 }}>
                {selected.size} selected
              </Typography>
              <Box sx={{ width: 1, height: 20, background: 'var(--divider)' }} />
              <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', flexShrink: 0 }}>Change:</Typography>
              <select value={bulkField} onChange={e => {
                const meta = BULK_FIELDS.find(f => f.value === e.target.value)!;
                setBulkField(e.target.value);
                if (meta.type === 'boolean') setBulkValue('false');
                else if (meta.type === 'company') setBulkValue(companies[0]?.name ?? '');
                else setBulkValue('0');
              }} style={{ ...inpSx } as React.CSSProperties}>
                {BULK_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', flexShrink: 0 }}>to:</Typography>
              {currentBulkFieldMeta.type === 'company' && (
                <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{ ...inpSx } as React.CSSProperties}>
                  {companies.map(c => (
                    <option key={c.id} value={c.name}>
                      {c.display_name || c.name}
                    </option>
                  ))}
                </select>
              )}
              {currentBulkFieldMeta.type === 'boolean' && (
                <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{ ...inpSx } as React.CSSProperties}>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              )}
              {currentBulkFieldMeta.type === 'orient' && (
                <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} style={{ ...inpSx } as React.CSSProperties}>
                  {ORIENT_LABELS.map((l, i) => <option key={i} value={String(i)}>{l}</option>)}
                </select>
              )}
              {currentBulkFieldMeta.type === 'number' && (
                <input type="number" min={0} value={bulkValue} onChange={e => setBulkValue(e.target.value)}
                  style={{ ...inpSx, width: 70 } as React.CSSProperties} />
              )}
              <Box component="button" onClick={handleBulkApply} disabled={bulkApplying}
                sx={{ ml: 'auto', px: '14px', py: '5px', borderRadius: '7px', fontSize: 12, fontWeight: 600, border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', cursor: 'pointer', opacity: bulkApplying ? 0.6 : 1, '&:hover:not(:disabled)': { background: 'var(--accent-light)' } }}>
                {bulkApplying ? 'Applying…' : 'Apply'}
              </Box>
              <Box component="button" onClick={() => setSelected(new Set())}
                sx={{ px: '10px', py: '5px', borderRadius: '7px', fontSize: 12, border: '1px solid var(--glass-border)', background: 'transparent', color: 'var(--text-disabled)', cursor: 'pointer', '&:hover': { color: 'var(--text-secondary)' } }}>
                Clear
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Unregistered tab */}
      {tab === 'unregistered' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto', flex: 1 }}>

          {/* Orphaned (deleted) */}
          <Box sx={glassCard}>
            <Box sx={{ px: '18px', py: '10px', borderBottom: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>Orphaned devices</Typography>
              <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>Were active, then deleted from portal</Typography>
            </Box>
            {orphans.length === 0 ? (
              <Box sx={{ px: '18px', py: '16px' }}><Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>No orphaned devices</Typography></Box>
            ) : (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: '32px 1fr 120px 90px 120px 100px 60px', px: '14px', py: '7px', background: 'var(--glass-hover)', borderBottom: '1px solid var(--divider)' }}>
                  {['', 'Device ID', 'IP', 'Version', 'First seen', 'Last seen', ''].map((h, i) => (
                    <Typography key={i} sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)' }}>{h}</Typography>
                  ))}
                </Box>
                {orphans.map(o => (
                  <Box key={o.device_id} sx={{ display: 'grid', gridTemplateColumns: '32px 1fr 120px 90px 120px 100px 60px', px: '14px', py: '9px', borderBottom: '1px solid var(--divider)', alignItems: 'center', '&:hover': { background: 'var(--row-hover)' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: o.online ? '#4ade80' : '#f87171', boxShadow: o.online ? '0 0 6px #4ade80' : 'none' }} />
                    </Box>
                    <Typography sx={{ fontSize: 12, fontFamily: '"JetBrains Mono", monospace', color: '#fbbf24', fontWeight: 600 }}>{o.device_id}</Typography>
                    <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace' }}>{o.ip_address ?? '—'}</Typography>
                    <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)', fontFamily: '"JetBrains Mono", monospace' }}>{o.app_version ?? '—'}</Typography>
                    <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{fmtDate(o.first_seen_at)}</Typography>
                    <Typography sx={{ fontSize: 12, color: o.online ? '#4ade80' : 'var(--text-disabled)' }}>{fmtRelative(o.last_seen_at)}</Typography>
                    <Box component="button" onClick={() => handleDeleteOrphan(o.device_id)}
                      sx={{ fontSize: 11, px: '7px', py: '3px', borderRadius: '6px', border: '1px solid rgba(248,113,113,.25)', background: 'transparent', color: '#f87171', cursor: 'pointer', fontFamily: 'inherit', '&:hover': { background: 'rgba(248,113,113,.08)' } }}>
                      Remove
                    </Box>
                  </Box>
                ))}
              </>
            )}
          </Box>

          {/* Pending activation */}
          <Box sx={glassCard}>
            <Box sx={{ px: '18px', py: '10px', borderBottom: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>Pending activation</Typography>
              <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>Devices waiting for portal activation</Typography>
            </Box>
            {pending.length === 0 ? (
              <Box sx={{ px: '18px', py: '16px' }}><Typography sx={{ fontSize: 13, color: 'var(--text-disabled)' }}>No pending activations</Typography></Box>
            ) : (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 160px 120px', px: '14px', py: '7px', background: 'var(--glass-hover)', borderBottom: '1px solid var(--divider)' }}>
                  {['Activation code', 'Created', 'Expires'].map((h, i) => (
                    <Typography key={i} sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)' }}>{h}</Typography>
                  ))}
                </Box>
                {pending.map(p => (
                  <Box key={p.activation_code} sx={{ display: 'grid', gridTemplateColumns: '1fr 160px 120px', px: '14px', py: '9px', borderBottom: '1px solid var(--divider)', alignItems: 'center', '&:hover': { background: 'var(--row-hover)' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography sx={{ fontSize: 16, fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, letterSpacing: '0.15em', color: p.expired ? 'var(--text-disabled)' : 'var(--accent-light)' }}>
                        {p.activation_code}
                      </Typography>
                      {!p.expired && (
                        <Box sx={{ fontSize: 10, fontWeight: 700, px: '6px', py: '2px', borderRadius: '6px', background: 'rgba(74,222,128,.1)', color: '#4ade80', border: '1px solid rgba(74,222,128,.2)' }}>waiting</Box>
                      )}
                      {p.expired && (
                        <Box sx={{ fontSize: 10, fontWeight: 700, px: '6px', py: '2px', borderRadius: '6px', background: 'rgba(148,163,184,.1)', color: '#94a3b8', border: '1px solid rgba(148,163,184,.2)' }}>expired</Box>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{fmtDate(p.created_at)}</Typography>
                    <Typography sx={{ fontSize: 11, color: p.expired ? '#f87171' : 'var(--text-secondary)' }}>{fmtDate(p.expires_at)}</Typography>
                  </Box>
                ))}
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Add Device modal */}
      {addOpen && (
        <Box sx={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}
          onClick={() => setAddOpen(false)}>
          <Box sx={{ background: 'var(--glass-bg)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', border: '1px solid var(--glass-border)', borderRadius: '16px', width: 380, maxWidth: '90vw' }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <Box sx={{ px: '20px', py: '14px', borderBottom: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Add Device</Typography>
              <Box component="button" onClick={() => setAddOpen(false)}
                sx={{ width: 26, height: 26, borderRadius: '50%', border: '1px solid var(--glass-border)', background: 'transparent', color: 'var(--text-disabled)', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit', '&:hover': { background: 'var(--glass-hover)', color: 'var(--text-secondary)' } }}>
                ✕
              </Box>
            </Box>

            {/* Body */}
            <Box sx={{ px: '20px', py: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.06em', mb: '5px' }}>Display Name</Typography>
                <input
                  value={addName}
                  onChange={e => setAddName(e.target.value)}
                  placeholder="Leave empty to auto-name"
                  style={{ ...inpSx, width: '100%', boxSizing: 'border-box', padding: '7px 10px' } as React.CSSProperties}
                  autoFocus
                />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.06em', mb: '5px' }}>Company</Typography>
                <select
                  value={addCompany}
                  onChange={e => setAddCompany(e.target.value)}
                  style={{ ...inpSx, width: '100%', boxSizing: 'border-box', padding: '7px 10px' } as React.CSSProperties}>
                  <option value="">— No company —</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.name}>{c.display_name || c.name}</option>
                  ))}
                </select>
              </Box>
              <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>
                Device ID is auto-generated. The device can still activate itself and connect to this record.
              </Typography>
            </Box>

            {/* Footer */}
            <Box sx={{ px: '20px', py: '14px', borderTop: '1px solid var(--glass-border)', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Box component="button" onClick={() => setAddOpen(false)}
                sx={{ px: '14px', py: '6px', borderRadius: '8px', border: '1px solid var(--glass-border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', '&:hover': { background: 'var(--glass-hover)' } }}>
                Cancel
              </Box>
              <Box component="button" onClick={handleCreate} disabled={addSaving}
                sx={{ px: '14px', py: '6px', borderRadius: '8px', border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', opacity: addSaving ? 0.6 : 1, '&:hover:not(:disabled)': { background: 'var(--accent-light)' } }}>
                {addSaving ? 'Creating…' : 'Create'}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

    </Box>
  );
};

export default AdminDevicesPage;
